/**
 * OAuth callbacks and the login form build their next address from a value
 * that ultimately comes from a query parameter or stored metadata. A
 * candidate like "@evil.com" spliced after the origin turns into
 * "http://host@evil.com", which browsers resolve to evil.com. Only a
 * same-origin path is safe. Browsers also normalize backslashes, remove some
 * control characters and collapse dot segments, so checking the first two
 * characters alone does not cover every way to introduce a second host.
 *
 * Dependency-free on purpose: the login form (a client component) imports it.
 */
export function safeReturnTo(candidate: unknown, fallback: string): string {
  if (typeof candidate !== "string" || candidate.length === 0) return fallback;
  if (candidate[0] !== "/") return fallback;
  if (candidate[1] === "/") return fallback;
  for (const character of candidate) {
    const code = character.charCodeAt(0);
    if (character === "\\" || code <= 0x1f || code === 0x7f) return fallback;
  }

  try {
    // Only relative paths are accepted, so a fixed origin is sufficient to
    // validate browser URL semantics without environment or server imports.
    const origin = "https://return.invalid";
    const destination = new URL(candidate, origin);
    if (destination.origin !== origin || destination.pathname.startsWith("//")) return fallback;
    // Recheck above after normalization: /path/..//host becomes //host,
    // which would leave the origin when handed to a client router as a path.
    return `${destination.pathname}${destination.search}${destination.hash}`;
  } catch {
    return fallback;
  }
}
